/**
 * useLocationPaths — resuelve la ruta completa de una ubicación en los listados.
 *
 * Un listado recibe `location_name` (solo el nombre suelto), que no dice nada
 * cuando hay varias sedes con «Sala 1». Este hook carga el árbol de ubicaciones
 * una vez y devuelve `pathFor(id)`, que compone «Sede › Planta 2 › Sala 3».
 *
 * Uso:
 *   const { pathFor } = useLocationPaths(companyId);
 *   pathFor(a.location_id) || a.location_name || '—'
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { buildLocationMap, locationPath } from '../utils/locationTree';

export default function useLocationPaths(companyId) {
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    let vivo = true;
    api.getLocations('', companyId, { includeInactive: true })
      // `GET /api/locations` responde `{ success, data, count }`, no un array
      // suelto. Aceptar las dos formas evita que el árbol llegue siempre vacío
      // (era el motivo de que los filtros de ubicación salieran sin opciones).
      .then((res) => {
        if (!vivo) return;
        const rows = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setLocations(rows);
      })
      .catch(() => { if (vivo) setLocations([]); });
    return () => { vivo = false; };
  }, [companyId]);

  const mapa = useMemo(() => buildLocationMap(locations), [locations]);

  const pathFor = useMemo(
    () => (locationId) => (locationId ? locationPath(mapa, locationId) || '' : ''),
    [mapa],
  );

  return { locations, mapa, pathFor };
}
